import { useInfiniteQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { CreditCard, Loader2, Receipt, RotateCcw, Star } from "lucide-react";
import { useAuth } from "@/context/auth";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { formatCurrency } from "@/lib/currency";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");
const PAGE_SIZE = 25;

type HistoryEvent = {
  id: string;
  kind: "sale" | "return" | "credit_payment" | "loyalty";
  occurredAt: string;
  amount?: number;
  pointsDelta?: number;
  description: string;
  receiptNumber?: string;
  status?: string;
};

type HistoryResponse = {
  customer: { id: number; name: string; email: string | null; phone: string | null; loyaltyPoints: number };
  summary: { completedSalesCount: number; totalSpent: number; totalOutstanding: number };
  events: HistoryEvent[];
  hasMore: boolean;
  nextCursor: string | null;
  historyNotice: string;
};

const EVENT_LABELS = {
  sale: "Sale",
  return: "Return",
  credit_payment: "Credit payment",
  loyalty: "Loyalty",
} as const;

const EVENT_ICONS = {
  sale: Receipt,
  return: RotateCcw,
  credit_payment: CreditCard,
  loyalty: Star,
} as const;

export function CustomerHistoryDialog({ customerId, onClose }: { customerId: number | null; onClose: () => void }) {
  const { user, store } = useAuth();
  const history = useInfiniteQuery({
    queryKey: ["customer-history", store?.id, user?.id, customerId],
    enabled: customerId !== null && !!user && !!store,
    initialPageParam: null as string | null,
    queryFn: async ({ pageParam }): Promise<HistoryResponse> => {
      const params = new URLSearchParams({ limit: String(PAGE_SIZE) });
      if (pageParam) params.set("cursor", pageParam);
      const res = await fetch(`${BASE}/api/customers/${customerId}/history?${params}`, {
        credentials: "include",
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || "Could not load customer history");
      }
      return res.json();
    },
    getNextPageParam: (lastPage) => lastPage.hasMore ? lastPage.nextCursor ?? undefined : undefined,
  });

  const firstPage = history.data?.pages[0];
  const events = history.data?.pages.flatMap(page => page.events) ?? [];

  return (
    <Dialog open={customerId !== null} onOpenChange={open => { if (!open) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Customer history</DialogTitle>
        </DialogHeader>
        {history.isPending ? (
          <div className="flex justify-center py-12" data-testid="status-customer-history-loading">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : history.isError && !firstPage ? (
          <div className="space-y-3 py-8 text-center">
            <p className="text-sm text-destructive" data-testid="status-customer-history-error">{history.error.message}</p>
            <Button variant="outline" onClick={() => history.refetch()} data-testid="button-retry-customer-history">Try again</Button>
          </div>
        ) : firstPage ? (
          <div className="space-y-5">
            <div>
              <h3 className="text-lg font-semibold" data-testid="text-history-customer-name">{firstPage.customer.name}</h3>
              <p className="text-sm text-muted-foreground">
                {[firstPage.customer.phone, firstPage.customer.email].filter(Boolean).join(" · ") || "No contact details"}
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <div className="rounded-lg border p-3">
                <p className="text-xs text-muted-foreground">Completed sales</p>
                <p className="font-semibold" data-testid="text-history-sale-count">{firstPage.summary.completedSalesCount}</p>
              </div>
              <div className="rounded-lg border p-3">
                <p className="text-xs text-muted-foreground">Net recorded spend</p>
                <p className="font-semibold" data-testid="text-history-net-spend">{formatCurrency(firstPage.summary.totalSpent)}</p>
              </div>
              <div className="rounded-lg border p-3">
                <p className="text-xs text-muted-foreground">Credit outstanding</p>
                <p className="font-semibold" data-testid="text-history-credit-balance">{formatCurrency(firstPage.summary.totalOutstanding)}</p>
              </div>
              <div className="rounded-lg border p-3">
                <p className="text-xs text-muted-foreground">Current loyalty points</p>
                <p className="font-semibold" data-testid="text-history-loyalty-balance">{firstPage.customer.loyaltyPoints}</p>
              </div>
            </div>
            <p className="rounded-lg border bg-muted/40 px-3 py-2 text-xs text-muted-foreground" data-testid="status-history-coverage">
              {firstPage.historyNotice}
            </p>
            <div>
              <h4 className="mb-3 text-sm font-semibold">Activity</h4>
              {events.length === 0 ? (
                <p className="rounded-lg border px-4 py-8 text-center text-sm text-muted-foreground" data-testid="status-customer-history-empty">
                  No recorded activity for this customer yet.
                </p>
              ) : (
                <div className="divide-y rounded-lg border">
                  {events.map(event => {
                    const Icon = EVENT_ICONS[event.kind];
                    return (
                      <div key={event.id} className="flex items-start gap-3 p-3" data-testid={`row-customer-event-${event.id}`}>
                        <div className="rounded-md bg-muted p-2"><Icon className="h-4 w-4 text-muted-foreground" /></div>
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="text-sm font-medium">{EVENT_LABELS[event.kind]}</span>
                            {event.status && <Badge variant="outline" className="text-xs">{event.status}</Badge>}
                          </div>
                          <p className="text-sm text-muted-foreground">{event.description}</p>
                          <p className="mt-1 text-xs text-muted-foreground">
                            {format(new Date(event.occurredAt), "MMM d, yyyy · h:mm a")}
                            {event.receiptNumber ? ` · ${event.receiptNumber}` : ""}
                          </p>
                        </div>
                        <div className="shrink-0 text-right text-sm font-medium">
                          {event.kind === "loyalty" && event.pointsDelta !== undefined
                            ? `${event.pointsDelta > 0 ? "+" : ""}${event.pointsDelta} pts`
                            : event.amount !== undefined
                              ? formatCurrency(Math.abs(event.amount))
                              : null}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              {history.hasNextPage && (
                <Button
                  variant="outline"
                  className="mt-3 w-full"
                  disabled={history.isFetchingNextPage}
                  onClick={() => history.fetchNextPage()}
                  data-testid="button-load-more-customer-history"
                >
                  {history.isFetchingNextPage ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                  Load more activity
                </Button>
              )}
              {history.isFetchNextPageError && (
                <p className="mt-2 text-sm text-destructive" data-testid="status-history-more-error">
                  Could not load more activity. Try again.
                </p>
              )}
            </div>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}